import { Home, Package, Users, ShoppingCart, FileText, Settings, LogOut, Building2, DollarSign, Shield, UserCog, Key, MessageCircle, FileEdit, ChevronRight, FolderTree, Wallet, Workflow, Activity, Brain, User, ClipboardList, ArrowDownCircle, CreditCard, Tag } from "lucide-react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  useSidebarContext,
  SidebarMenuSkeleton,
} from "@/components/ui/sidebar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTenant } from "@/contexts/TenantContext";
import { usePermissions } from "@/hooks/usePermissions";
import { useChatwootToken } from "@/hooks/use-chatwoot-token";
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";

type MenuItem = {
  title: string;
  url?: string;
  icon: React.ComponentType<{ className?: string }>;
  module?: string;
  subItems?: MenuItem[];
};

const menuItems: MenuItem[] = [
  { title: "Visão Geral", url: "/dashboard", icon: Home, module: "dashboard" },
  { title: "Relatórios", url: "/relatorios", icon: FileText, module: "reports" },
  { title: "Caixa", url: "/caixa", icon: DollarSign, module: "transactions" },
  { title: "Registrar Venda", url: "/pdv", icon: ShoppingCart, module: "transactions" },
  { title: "Chatwoot", url: "/chatwoot/abrir", icon: MessageCircle },
  {
    title: "IA",
    url: "/ia",
    icon: Brain,
    subItems: [
      { title: "Minhas IAs", url: "/ia/ia-agentes", icon: Brain },
      { title: "Treinamento IA", url: "/ia/treinamento-ia", icon: Brain },
    ],
  },
  { 
    title: "Cadastros", 
    url: "/cadastros", 
    icon: ClipboardList,
    subItems: [
      { title: "Produtos", url: "/produtos", icon: Package, module: "products" },
      { title: "Clientes", url: "/clientes", icon: Users, module: "clients" },
      { title: "Fornecedores", url: "/fornecedores", icon: Building2, module: "suppliers" },
      { title: "Usuários", url: "/usuarios", icon: UserCog, module: "users" },
      { title: "Categorias", url: "/categorias", icon: FolderTree },
    ]
  },
  { 
    title: "Configurações", 
    url: "/configuracoes", 
    icon: Settings,
    subItems: [
      { title: "Empresa", url: "/configuracoes/empresa", icon: Building2 },
      { title: "Meu Perfil", url: "/configuracoes/perfil", icon: User },
      { title: "Chatwoot", url: "/configuracoes/chatwoot", icon: MessageCircle },
      { title: "Assinatura", url: "/configuracoes/assinatura", icon: CreditCard },
      { title: "Contas Bancárias", url: "/configuracoes/contas-bancarias", icon: Wallet },
      { title: "Integrações", url: "/configuracoes/integracoes", icon: Workflow },
      { title: "Logs Avançados", url: "/configuracoes/logs-avancados", icon: Activity },
      { title: "Formulários", url: "/configuracoes/formularios", icon: FileEdit },
      { title: "IA Defaults", url: "/configuracoes/ia-defaults", icon: Brain },
      { title: "API Reference", url: "/configuracoes/api-reference", icon: FileText },
      
    ]
  },
];

const adminSaasItems = [
  { title: "Visão Geral SaaS", url: "/saas/visao-geral", icon: Activity },
  { title: "Clientes SaaS", url: "/clientes-saas", icon: Building2, module: "tenants" },
  { title: "Assinaturas", url: "/assinaturas", icon: CreditCard },
  { title: "Transações", url: "/transacoes", icon: ArrowDownCircle },
  { title: "Cupons", url: "/cupons", icon: Tag },
];

export function AppSidebar({ onNavigate }: { onNavigate?: () => void }) {
  const sidebar = useSidebarContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdminSaas, isImpersonating, tenantName, tenantImages } = useTenant();
  const { canAccessModule, isLoading: permissionsLoading } = usePermissions();
  const { hasToken } = useChatwootToken();
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());

  // Para mobile (quando usado dentro do Sheet), não há estado de sidebar
  const isMobileMode = !sidebar;
  const sidebarState = sidebar?.state || "expanded";
  const isCollapsed = sidebarState === "collapsed" && !isMobileMode;

  // Expandir menu automaticamente se estiver em uma rota filha
  useEffect(() => {
    const next = new Set<string>();
    if (location.pathname.startsWith("/configuracoes") || location.pathname.startsWith("/formularios")) {
      next.add("Configurações");
    }
    if (location.pathname.startsWith("/produtos") || 
        location.pathname.startsWith("/clientes") || 
        location.pathname.startsWith("/fornecedores") || 
        location.pathname.startsWith("/usuarios")) {
      next.add("Cadastros");
    }
    menuItems.forEach((item) => {
      if (item.subItems && item.subItems.length > 0) {
        const shouldExpand = item.subItems.some((s) => s.url && location.pathname.startsWith(s.url));
        if (shouldExpand) next.add(item.title);
      }
    });
    setExpandedMenus(next);
  }, [location.pathname]);

  const toggleMenu = (title: string) => {
    setExpandedMenus((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(title)) {
        newSet.delete(title);
      } else {
        newSet.add(title);
      }
      return newSet;
    });
  };

  // Função para verificar se uma rota está ativa (ignora query string completamente)
  const isRouteActive = (url: string | undefined): boolean => {
    if (!url || url === "#") return false;
    const currentPath = location.pathname;
    const menuPath = url.split("?")[0];

    // match exato
    if (currentPath === menuPath) return true;

    // match para rotas filhas: começa com menuPath e próximo char é "/"
    if (menuPath !== "/" && currentPath.startsWith(menuPath)) {
      const nextChar = currentPath[menuPath.length];
      if (!nextChar || nextChar === "/") return true;
    }

    return false;
  };

  const handleSignOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast.error("Erro ao sair");
    } else {
      await import("@/services/activities").then(m => m.logAuth("logout", "manual"));
      navigate("/auth");
    }
  };

  const handleNavClick = (itemTitle?: string) => {
    // Não fechar o menu para "Registrar Venda"
    if (itemTitle === "Registrar Venda") {
      return;
    }
    if (onNavigate) {
      onNavigate();
    }
  };

  // Conteúdo do sidebar (mesmo para mobile e desktop)
  const sidebarContent = (
    <>
      <div className="px-2 pt-4 pb-2">
        {(isMobileMode || sidebarState !== "collapsed") && (
          <>
            <div className="w-full mb-1">
              <img 
                src="/logo-fundo-branco.png" 
                alt="Logo" 
                id="logo"
                className="w-full h-auto object-contain"
              />
            </div>
          </>
        )}
        {isCollapsed && !isMobileMode && (
          <div className="flex items-center justify-center mb-1 px-2">
            <img 
              src="/logo-fundo-branco.png" 
              alt="Logo" 
              id="logo"
              className="w-full h-auto object-contain"
            />
          </div>
        )}
      </div>

      {/* Menus específicos para Admin SaaS */}
      {isAdminSaas && !isImpersonating ? (
        <SidebarGroup>
          <SidebarGroupLabel>Gestão SaaS</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {adminSaasItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={item.url}
                      onClick={() => handleNavClick(item.title)}
                                        className={isRouteActive(item.url)
                                            ? "bg-white text-orange-900 font-bold rounded-l-[15px] rounded-r-none pr-0 mr-[-3px] hover:!bg-white hover:!text-orange-900 transition-colors duration-300"
                                            : "hover:bg-sidebar-accent/50 transition-colors duration-300"
                                        }
                    >
                      <item.icon className="h-4 w-4" />
                      {(isMobileMode || sidebarState !== "collapsed") && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
              {/* Menu de perfis e permissões globais para Admin SaaS */}
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <NavLink
                    to="/perfis"
                    onClick={() => handleNavClick("Perfis")}
                                        className={isRouteActive("/perfis")
                                            ? "bg-white text-orange-900 font-bold rounded-l-[15px] rounded-r-none pr-0 mr-[-3px] hover:!bg-white hover:!text-orange-900 transition-colors duration-300"
                                            : "hover:bg-sidebar-accent/50 transition-colors duration-300"
                                        }
                  >
                    <Shield className="h-4 w-4" />
                    {(isMobileMode || sidebarState !== "collapsed") && <span>Perfis</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <NavLink
                    to="/permissoes"
                    onClick={() => handleNavClick("Permissões")}
                                        className={isRouteActive("/permissoes")
                                            ? "bg-white text-orange-900 font-bold rounded-l-[15px] rounded-r-none pr-0 mr-[-3px] hover:!bg-white hover:!text-orange-900 transition-colors duration-300"
                                            : "hover:bg-sidebar-accent/50 transition-colors duration-300"
                                        }
                  >
                    <Key className="h-4 w-4" />
                    {(isMobileMode || sidebarState !== "collapsed") && <span>Permissões</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      ) : (
        /* Menus normais para usuários de tenant */
        <SidebarGroup>
          <SidebarGroupLabel data-cy="menu-principal-label">Menu Principal</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu data-cy="menu-principal">
              {permissionsLoading
                ? Array.from({ length: 4 }).map((_, index) => (
                    <SidebarMenuItem key={`skeleton-${index}`}>
                      <SidebarMenuSkeleton showIcon />
                    </SidebarMenuItem>
                  ))
                : menuItems
                    .filter((item) => !item.module || canAccessModule(item.module))
                    .map((item, index) => {
                      const hasSubItems = item.subItems && item.subItems.length > 0;
                      const isExpanded = expandedMenus.has(item.title);
                      const isCollapsed = sidebarState === "collapsed" && !isMobileMode;

                      const isFirstItem = index === 0;
                      const isCadastros = item.title === "Cadastros";
                      const isChatwoot = item.title === "Chatwoot";
                      
                      return (
                        <SidebarMenuItem 
                          key={item.title} 
                          className={
                            item.title === "Caixa" || isCadastros || isFirstItem || isChatwoot
                              ? "mt-3" 
                              : ""
                          }
                        >
                          {hasSubItems ? (
                            <>
                              {isCollapsed && !isMobileMode ? (
                                <TooltipProvider delayDuration={0}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <SidebarMenuButton
                                        onClick={() => {
                                          toggleMenu(item.title);
                                        }}
                                        className={cn(
                                          "w-full",
                                          isExpanded && "bg-gray-700 text-sidebar-foreground rounded-l-[15px] rounded-r-none pr-0 mr-[-3px]"
                                        )}
                                        data-cy={`nav-${item.title.toLowerCase().replace(' ', '-')}`}
                                      >
                                        <item.icon className="h-4 w-4" />
                                      </SidebarMenuButton>
                                    </TooltipTrigger>
                                    <TooltipContent
                                      side="right"
                                      align="center"
                                      sideOffset={0}
                                      className="ml-0 rounded-l-none rounded-r-md bg-sidebar border-sidebar-border border-l-0 px-3 py-2 text-sidebar-foreground shadow-lg !z-[9999]"
                                    >
                                      {item.title}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : (
                                <SidebarMenuButton
                                  onClick={() => {
                                    toggleMenu(item.title);
                                  }}
                                  className={cn(
                                    "w-full",
                                    isExpanded && "bg-sidebar-accent text-sidebar-accent-foreground"
                                  )}
                                  data-cy={`nav-${item.title.toLowerCase().replace(' ', '-')}`}
                                >
                                  <item.icon className="h-4 w-4" />
                                  {(isMobileMode || !isCollapsed) && (
                                    <>
                                      <span className="flex-1 text-left">{item.title}</span>
                                      <ChevronRight
                                        className={cn(
                                          "h-4 w-4 transition-transform",
                                          isExpanded && "rotate-90"
                                        )}
                                      />
                                    </>
                                  )}
                                </SidebarMenuButton>
                              )}
                              {isExpanded && item.subItems && (
                                <>
                                  {!isCollapsed ? (
                                    <SidebarMenu className="ml-3 mr-0 mt-1 w-[calc(100%-0.75rem+1px)]">
                                      {item.subItems
                                        .filter((subItem) => {
                                          if (subItem.title === "IA Defaults" && !isAdminSaas) return false;
                                          if (subItem.title === "Minhas IAs" && isAdminSaas) return false;
                                          return !subItem.module || canAccessModule(subItem.module);
                                        })
                                        .map((subItem) => (
                                          <SidebarMenuItem key={subItem.title}>
                                            <SidebarMenuButton asChild>
                                              <NavLink
                                                to={subItem.url || "#"}
                                                onClick={() => handleNavClick(subItem.title)}
                                        className={isRouteActive(subItem.url)
                                            ? "bg-white text-orange-900 font-bold rounded-l-[15px] rounded-r-none pr-0 mr-[-3px] hover:!bg-white hover:!text-orange-900 transition-colors duration-300"
                                            : "hover:bg-sidebar-accent/50 transition-colors duration-300"
                                        }
                                                data-cy={`nav-${subItem.title.toLowerCase().replace(' ', '-')}`}
                                              >
                                                <subItem.icon className="h-4 w-4" />
                                                <span>{subItem.title}</span>
                                              </NavLink>
                                            </SidebarMenuButton>
                                          </SidebarMenuItem>
                                        ))}
                                    </SidebarMenu>
                                  ) : (
                                    <TooltipProvider delayDuration={0}>
                                      <SidebarMenu className="mt-1 flex flex-col gap-1">
                                        {item.subItems
                                          .filter((subItem) => {
                                            if (subItem.title === "IA Defaults" && !isAdminSaas) return false;
                                            if (subItem.title === "Minhas IAs" && isAdminSaas) return false;
                                            return !subItem.module || canAccessModule(subItem.module);
                                          })
                                          .map((subItem) => (
                                            <SidebarMenuItem key={subItem.title}>
                                              <Tooltip>
                                                <TooltipTrigger asChild>
                                                  <SidebarMenuButton asChild className="w-full justify-center p-2">
                                                    <NavLink
                                                      to={subItem.url || "#"}
                                                      onClick={() => handleNavClick(subItem.title)}
                                        className={isRouteActive(subItem.url)
                                            ? "bg-white text-orange-900 font-bold rounded-l-[15px] rounded-r-none pr-0 mr-[-3px] hover:!bg-white hover:!text-orange-900 transition-colors duration-300"
                                            : "hover:bg-sidebar-accent/50 transition-colors duration-300"
                                        }
                                                      data-cy={`nav-${subItem.title.toLowerCase().replace(' ', '-')}`}
                                                    >
                                                      <subItem.icon className="h-4 w-4" />
                                                    </NavLink>
                                                  </SidebarMenuButton>
                                                </TooltipTrigger>
                                                <TooltipContent
                                                  side="right"
                                                  align="center"
                                                  sideOffset={0}
                                                  className="ml-0 rounded-l-none rounded-r-md bg-sidebar border-sidebar-border border-l-0 px-3 py-2 text-sidebar-foreground shadow-lg z-[100]"
                                                >
                                                  {subItem.title}
                                                </TooltipContent>
                                              </Tooltip>
                                            </SidebarMenuItem>
                                          ))}
                                      </SidebarMenu>
                                    </TooltipProvider>
                                  )}
                                </>
                              )}
                            </>
                          ) : item.title === "Chatwoot" ? (
                            isCollapsed && !isMobileMode ? (
                              <TooltipProvider delayDuration={0}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <SidebarMenuButton
                                      onClick={() => {
                                        handleNavClick();
                                        if (hasToken) {
                                          window.open(item.url, "_blank", "noopener,noreferrer");
                                        } else {
                                          navigate(item.url || "/");
                                        }
                                      }}
                                      data-cy="nav-chatwoot"
                                    >
                                      <item.icon className="h-4 w-4" />
                                    </SidebarMenuButton>
                                  </TooltipTrigger>
                                  <TooltipContent
                                    side="right"
                                    align="center"
                                    sideOffset={0}
                                    className="ml-0 rounded-l-none rounded-r-md bg-sidebar border-sidebar-border border-l-0 px-3 py-2 text-sidebar-foreground shadow-lg z-50"
                                  >
                                    {item.title}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : (
                              <SidebarMenuButton
                                onClick={() => {
                                  handleNavClick();
                                  if (hasToken) {
                                    window.open(item.url, "_blank", "noopener,noreferrer");
                                  } else {
                                    navigate(item.url || "/");
                                  }
                                }}
                                data-cy="nav-chatwoot"
                              >
                                <item.icon className="h-4 w-4" />
                                {(isMobileMode || !isCollapsed) && <span>{item.title}</span>}
                              </SidebarMenuButton>
                            )
                          ) : (
                            isCollapsed && !isMobileMode ? (
                              <TooltipProvider delayDuration={0}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <SidebarMenuButton asChild>
                                      <NavLink
                                        to={item.url || "#"}
                                        end={item.url === "/dashboard"}
                                        onClick={() => handleNavClick(item.title)}
                                        className={isRouteActive(item.url)
                                            ? "bg-white text-orange-900 font-bold rounded-l-[15px] rounded-r-none pr-0 mr-[-3px] hover:!bg-white hover:!text-orange-900 transition-colors duration-300"
                                            : "hover:bg-sidebar-accent/50 transition-colors duration-300"
                                        }
                                        data-cy={`nav-${item.title.toLowerCase().replace(' ', '-')}`}
                                      >
                                        <item.icon className="h-4 w-4" />
                                      </NavLink>
                                    </SidebarMenuButton>
                                  </TooltipTrigger>
                                  <TooltipContent
                                    side="right"
                                    align="center"
                                    sideOffset={0}
                                    className="ml-0 rounded-l-none rounded-r-md bg-sidebar border-sidebar-border border-l-0 px-3 py-2 text-sidebar-foreground shadow-lg z-50"
                                  >
                                    {item.title}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : (
                              <SidebarMenuButton asChild>
                                <NavLink
                                  to={item.url || "#"}
                                  end={item.url === "/dashboard"}
                                  onClick={() => handleNavClick(item.title)}
                                        className={isRouteActive(item.url)
                                            ? "bg-white text-orange-900 font-bold rounded-l-[15px] rounded-r-none pr-0 mr-[-3px] hover:!bg-white hover:!text-orange-900 transition-colors duration-300"
                                            : "hover:bg-sidebar-accent/50 transition-colors duration-300"
                                        }
                                  data-cy={`nav-${item.title.toLowerCase().replace(' ', '-')}`}
                                >
                                  <item.icon className="h-4 w-4" />
                                  {(isMobileMode || !isCollapsed) && <span>{item.title}</span>}
                                </NavLink>
                              </SidebarMenuButton>
                            )
                          )}
                        </SidebarMenuItem>
                      );
                    })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      )}

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={handleSignOut} data-cy="logout-button">
              <LogOut className="h-4 w-4" />
              {(isMobileMode || sidebarState !== "collapsed") && <span>Sair</span>}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </>
  );

  // Se for mobile (sem contexto de sidebar), retorna apenas o conteúdo
  if (isMobileMode) {
    return (
      <div className="flex flex-col h-full bg-sidebar text-sidebar-foreground">
        <SidebarContent>
          {sidebarContent}
        </SidebarContent>
      </div>
    );
  }

  // Desktop com sidebar normal
  return (
    <Sidebar className={sidebarState === "collapsed" ? "w-14" : "w-48"} collapsible="icon">
      <SidebarContent>
        {sidebarContent}
      </SidebarContent>
    </Sidebar>
  );
}
